import {
	Address,
	Attachment,
	Client,
	ClientContact,
	CustomField,
	CustomFieldType,
	CustomViewFilterCriteria,
	DefaultTimestampFormatWithTime,
	GlobalSearchEntityObj,
	GlobalSearchEntityType,
	HubspotPushModes,
	HubspotPushSettingsRequest,
	MyVendor,
	Opportunity,
	PaymentStatus,
	SalesActivityAutomationActionUpdatableFieldValue,
	SalesActivityAutomationActionUpdatableFieldValueType,
	Supplier,
	UserAccess,
	UserRole,
	VendorClient,
	VendorPartner,
	VendorUserRole,
	ZomentumEntities,
	ZomentumPSA
} from "@/Contracts";
import { IOptionProps } from "@atoms/ZSelect/interface";
import { IOptionProps as ICheckboxOptionProps } from "@atoms/ZCheckboxGroup/interface";
import { VendorIndustry } from "@/Contracts";
import moment, { Moment } from "moment";
import { WrappedFormUtils } from "@ant-design/compatible/lib/form/Form";
import { EColors } from "@atoms/ZColors/interface";
import { differenceBy } from "lodash";

const YOUTUBE_LINK_REGEX =
	/^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w\\-]+\?v=|embed\/|v\/)?)([\w\\-]+)(\S+)?$/g;

export const validateYoutubeLink = (val: string): RegExpMatchArray | null =>
	val.match(YOUTUBE_LINK_REGEX);

export const getYoutubeID = (youtubeURL: string): string | null => {
	const youtubeIdRegEx = new RegExp(
		/(?:\/|%3D|v=|vi=)([0-9A-z-_]{11})(?:[%#?&]|$)/gm
	);
	return youtubeIdRegEx.exec(youtubeURL)?.[1] ?? null;
};

export const validUrlRegex =
	/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;

export const checkValidUrl = (url: string): boolean => {
	const res = url.match(validUrlRegex);
	return !!res;
};

export const capitalizeAfterUnderscore = (
	input: string | undefined | null
): string => {
	if (!input || input.length === 0) {
		return "";
	}
	return input
		.split("_")
		.join(" ")
		.replace(/\b\w/g, (word: string) => word.toUpperCase());
};

export const customDateFormatter = (
	dateString: string | Date,
	format: string
): string => {
	const momentInstance = moment(dateString);
	const invalidFallback =
		dateString.toString() === "Invalid Date" ? "--" : dateString.toString();
	if (momentInstance.isValid()) {
		return momentInstance.format(format.replace("dd", "DD"));
	} else return invalidFallback;
};

export const capitalizeFirstLetter = (
	input: string | undefined | null
): string => {
	if (!input || input.length === 0) {
		return "";
	}
	return input.charAt(0).toUpperCase() + input.slice(1);
};

export const replaceWhiteSpaceWithNonBreakingSpace = (text: string): string => {
	return text?.replace(/ /g, "\u00a0");
};

export const convertEnumToSelectOptions = (
	record: Record<string, string>,
	optionsType: "checkboxGroup" | "select"
): IOptionProps[] | ICheckboxOptionProps[] => {
	if (optionsType === "checkboxGroup") {
		return Object.values(record).map(value => ({
			label: capitalizeAfterUnderscore(value),
			value: value
		}));
	}
	return Object.values(record).map(value => ({
		name: capitalizeAfterUnderscore(value),
		value: value
	}));
};

export const deepFreeze = (o: any) => {
	Object.freeze(o);

	Object.getOwnPropertyNames(o).forEach(function (prop) {
		if (
			Object.prototype.hasOwnProperty.call(o, prop) &&
			o[prop] !== null &&
			(typeof o[prop] === "object" || typeof o[prop] === "function") &&
			!Object.isFrozen(o[prop])
		) {
			deepFreeze(o[prop]);
		}
	});

	return o;
};

export const humanizeDateTime = (
	date: Date = new Date(),
	datePattern = ""
): string => {
	const shouldFormat =
		moment().subtract("6", "days").isAfter(moment(date)) ||
		moment().add("6", "days").isBefore(moment(date));
	if (shouldFormat) {
		return !!datePattern
			? customDateFormatter(date, datePattern)
			: moment(date).format(DefaultTimestampFormatWithTime);
	} else {
		return moment(date).calendar(null, {
			lastDay: "[Yesterday at] h:mm:ss A",
			sameDay: "[Today at] h:mm:ss A",
			nextDay: "[Tomorrow at] h:mm:ss A",
			lastWeek: "[last] dddd [at] h:mm:ss A",
			nextWeek: "dddd [at] h:mm:ss A",
			sameElse: DefaultTimestampFormatWithTime
		});
	}
};

/**
 *
 * @param arr1 the base array.
 * @param arr2 the modification array, the values from this array would override the value of base array
 * @param trimFromBase will trim on base element
 */
export const mergeArray = (
	arr1: (string | null)[],
	arr2: (string | null)[],
	trimFromBase = false
) =>
	new Array(trimFromBase ? arr1.length : Math.max(arr1.length, arr2.length))
		.fill(true)
		.map((_v, i) => arr1[i] || arr2[i] || null);

export const millimeterToPixel = (x: number): number => {
	const div = document.createElement("div");
	div.style.display = "block";
	div.style.width = `${x}mm`;
	document.body.appendChild(div);
	const px = parseFloat(window.getComputedStyle(div, null).width);
	div?.parentNode?.removeChild(div);
	return px;
};

export const roundTwoDecimals = (amount = 0): number => {
	if (!amount) return 0;
	const result = Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
	return isNaN(result) ? 0 : result;
};
export const fromEntries = (
	iterable: Iterable<readonly [string | number | symbol, any]>
): { [key: string]: string } => {
	if (Object.prototype.hasOwnProperty.call(Object, "fromEntries"))
		return Object.fromEntries(iterable);

	return Array.from(iterable).reduce<{ [key: string]: any }>(
		(obj, [key, val]) => {
			obj[key as string] = val;
			return obj;
		},
		{}
	);
};
export const customLocaleCompare = (
	a: string | null | undefined,
	b: string | undefined | null
): number => {
	return (!!a ? a : "").localeCompare(!!b ? b : "");
};

export const dateCompare = (a: Date | null, b: Date | null): number =>
	(!!a ? new Date(a).getTime() : 0) - (!!b ? new Date(b).getTime() : 0);

/**
 *
 * @param text string that needs to be parsed
 * @param count no of characters to be sliced to
 */
export const shortenText = (text: string, count = 0): string => {
	if (!!text && count && text.length > count)
		return text.slice(0, count).concat("...");
	return text;
};

export const getNearestPastFiveMinutes = (date: Date): moment.Moment => {
	const momentDate = moment(date);
	const nearestFiveMin = 5 * Math.floor(momentDate.minute() / 5);
	const remainder = momentDate.minute() - nearestFiveMin;
	const dateTime = moment(momentDate).subtract(remainder, "minutes");
	return dateTime;
};

export const getNearestHalfHour = (
	date: Date | moment.Moment
): moment.Moment => {
	const start = moment(date);
	const remainder = 30 - (start.minute() % 30);
	const dateTime = moment(start).add(remainder, "minutes");
	return dateTime;
};

export const getShippingAddressRowText = (address: Address): string => {
	const shippingAddress = [
		address?.address_line_1,
		address?.address_line_2,
		address?.city,
		address?.state,
		address?.country,
		address?.pincode
	]
		.filter(address => !!address?.trim()?.length)
		.join(", ");
	return shippingAddress;
};

export const getFileName = (attachment: Attachment): string => {
	const { file_name } = attachment;
	if (file_name && file_name.includes("."))
		return file_name.split(".").slice(0, -1).join(".");
	else return file_name;
};

export const getFileExtension = (attachment: Attachment): string => {
	const { file_name } = attachment;
	if (file_name && file_name.includes("."))
		return file_name.split(".").slice(-1).join("");
	else return file_name && file_name.slice(-3);
};

export const getFileExtensionFromUrl = (url: string): null | string => {
	const splitArr = url.split(".");
	if (splitArr) return splitArr[splitArr.length - 1];
	return null;
};

export const getMatchedAddressIndexInList = (
	addressList: Address[],
	searchAddress: Address
): number => {
	return addressList.findIndex(address => {
		// Compare basic address fields
		const basicFieldsMatch =
			address.address_line_1 === searchAddress.address_line_1 &&
			address.address_line_2 === searchAddress.address_line_2 &&
			address.city === searchAddress.city &&
			address.country === searchAddress.country &&
			address.state === searchAddress.state &&
			address.pincode === searchAddress.pincode;

		if (!basicFieldsMatch) {
			return false;
		}

		// Compare custom fields
		const addressCustomFields = address.custom_fields || [];
		const searchCustomFields = searchAddress.custom_fields || [];

		// If both have no custom fields, they match
		if (
			addressCustomFields.length === 0 &&
			searchCustomFields.length === 0
		) {
			return true;
		}

		// If one has custom fields and the other doesn't, they don't match
		if (addressCustomFields.length !== searchCustomFields.length) {
			return false;
		}

		// Compare each custom field
		for (const searchField of searchCustomFields) {
			const matchingField = addressCustomFields.find(
				f => f.id === searchField.id
			);
			if (!matchingField) {
				return false; // Custom field not found in address
			}

			// Compare values arrays
			const addressValues = matchingField.values || [];
			const searchValues = searchField.values || [];

			if (addressValues.length !== searchValues.length) {
				return false;
			}

			// Check if all values match
			for (let i = 0; i < addressValues.length; i++) {
				if (addressValues[i] !== searchValues[i]) {
					return false;
				}
			}
		}

		return true;
	});
};

export const convertSearchEntityTypeToOption = ({
	obj,
	entity_type
}: GlobalSearchEntityObj): SalesActivityAutomationActionUpdatableFieldValue<string> => {
	switch (entity_type) {
		case GlobalSearchEntityType.Client: {
			const result = obj as Client;
			return {
				id_value: result.id,
				display_value: result.name,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		case GlobalSearchEntityType.ClientContact: {
			const result = obj as ClientContact;
			return {
				id_value: result.id,
				display_value: result.name.full,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		case GlobalSearchEntityType.Opportunity: {
			const result = obj as Opportunity;
			return {
				id_value: result.id,
				display_value: result.name,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		case GlobalSearchEntityType.Supplier: {
			const result = obj as Supplier;
			return {
				id_value: result.id,
				display_value: result.name,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		case GlobalSearchEntityType.VendorCompany: {
			const result = obj as VendorPartner;
			return {
				id_value: result.id,
				display_value: result.name,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		case GlobalSearchEntityType.VendorPartner: {
			const result = obj as VendorPartner;
			return {
				id_value: result.id,
				display_value: result.name,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		case GlobalSearchEntityType.VendorClient: {
			const result = obj as VendorClient;
			return {
				id_value: result.id,
				display_value: result.name,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		case GlobalSearchEntityType.VendorCompanies: {
			const result = obj as MyVendor;
			return {
				id_value: result.id,
				display_value: result.name,
				value_type:
					SalesActivityAutomationActionUpdatableFieldValueType.Fixed
			};
		}

		default:
			throw new Error(`Invalid Entity Type: ${entity_type}`);
	}
};

export const calculateManualTotalRevenueFields = (
	form: WrappedFormUtils<any>,
	monthsToCalculateTotal: number
) => {
	let manualTotalMarginValue;
	let manualTotalTotalRevenueFieldValue;
	let manualTotalTotalCostFieldValue;
	const getFieldValue = (key: string) => {
		const value = form.getFieldValue(key)?.toString().trim() || "0";
		return +value;
	};
	if (!!monthsToCalculateTotal) {
		manualTotalTotalRevenueFieldValue =
			getFieldValue("manual_total.one_time") +
			getFieldValue("manual_total.weekly") * 4 * monthsToCalculateTotal +
			getFieldValue("manual_total.monthly") * monthsToCalculateTotal +
			(getFieldValue("manual_total.quarterly") / 3) *
				monthsToCalculateTotal +
			(getFieldValue("manual_total.semi_annually") / 6) *
				monthsToCalculateTotal +
			(getFieldValue("manual_total.annual") / 12) *
				monthsToCalculateTotal;

		manualTotalTotalCostFieldValue =
			getFieldValue("manual_total.one_time_cost") +
			getFieldValue("manual_total.weekly_cost") *
				4 *
				monthsToCalculateTotal +
			getFieldValue("manual_total.monthly_cost") *
				monthsToCalculateTotal +
			(getFieldValue("manual_total.quarterly_cost") / 3) *
				monthsToCalculateTotal +
			(getFieldValue("manual_total.semi_annually_cost") / 6) *
				monthsToCalculateTotal +
			(getFieldValue("manual_total.annual_cost") / 12) *
				monthsToCalculateTotal;

		manualTotalMarginValue =
			manualTotalTotalRevenueFieldValue - manualTotalTotalCostFieldValue;
	}

	return {
		manualTotalMarginValue,
		manualTotalTotalRevenueFieldValue,
		manualTotalTotalCostFieldValue
	};
};
export function getVendorIndustryDisplayName(
	industry: VendorIndustry | null
): string {
	switch (industry) {
		case VendorIndustry.SAAS:
			return "SaaS";
		default:
			return capitalizeAfterUnderscore(industry);
	}
}

export const processCustomFieldFormValues = (
	customFieldFormValues: Record<string, string | number | string[]>,
	existingCustomFields: CustomField[]
): CustomField[] => {
	const updatedCustomFieldList = new Array<CustomField>();
	Object.keys(customFieldFormValues).map(key => {
		const customField = existingCustomFields.find(
			field => field.id === key
		);
		if (customField) {
			const { creator_user, ...rest } = customField;
			// removing creator_user from custom field to reduce the payload size

			const updatedCustomField: CustomField = {
				...rest
			};
			if (updatedCustomField.field_type === CustomFieldType.DateField) {
				updatedCustomField.values = customFieldFormValues[key]
					? [
							new Date(
								customFieldFormValues[key].toString()
							).toISOString()
					  ]
					: new Array<string>();
			} else if (
				updatedCustomField.field_type === CustomFieldType.NumberField &&
				typeof customFieldFormValues[key] === "number"
			) {
				updatedCustomField.values =
					customFieldFormValues[key] !== null &&
					customFieldFormValues[key] >= 0
						? [customFieldFormValues[key].toString()]
						: new Array<string>();
			} else if (
				updatedCustomField.field_type === CustomFieldType.MultiSelect
			) {
				updatedCustomField.values = customFieldFormValues[
					key
				] as string[];
			} else {
				updatedCustomField.values = customFieldFormValues[key]
					? [customFieldFormValues[key].toString()]
					: new Array<string>();
			}
			updatedCustomFieldList.push(updatedCustomField);
		}
	});

	return updatedCustomFieldList;
};

export const isPartnerApp = (): boolean =>
	process.env.REACT_APP_NAME === "partnerApp";

export const isVendorApp = (): boolean =>
	process.env.REACT_APP_NAME === "vendorApp";

export const getPaymentInfoTagColor = (
	paymentStatus: PaymentStatus
): string => {
	switch (paymentStatus) {
		case PaymentStatus.Paid:
			return EColors.GREEN;
		case PaymentStatus.Failed:
			return EColors.RED;
		default:
			return EColors.BLUE;
	}
};

export const ZomentumPSADisplayText: Record<ZomentumPSA, string> = {
	[ZomentumPSA.ConnectWise]: "ConnectWise PSA",
	[ZomentumPSA.SuperOps]: "SuperOps",
	[ZomentumPSA.Autotask]: "Autotask",
	[ZomentumPSA.Kaseya]: "Kaseya BMS",
	[ZomentumPSA.Syncro]: "Syncro",
	[ZomentumPSA.RepairShopr]: "RepairShopr",
	[ZomentumPSA.Quickbooks]: "Quickbooks",
	[ZomentumPSA.Hubspot]: "HubSpot",
	[ZomentumPSA.HaloPSA]: "HaloPSA",
	[ZomentumPSA.InfusionSoft]: "Infusionsoft",
	[ZomentumPSA.Etilize]: "Etilize",
	[ZomentumPSA.Icecat]: "Icecat",
	[ZomentumPSA.Amazon]: "Amazon Business"
};

export const getPushModeValue = (
	hubspotPushSettings: HubspotPushSettingsRequest | null | undefined
) => {
	if (hubspotPushSettings?.when_opportunity_created_or_stage_updated) {
		return HubspotPushModes.WHEN_OPPORTUNITY_CREATE_OR_STAGE_UPDATED;
	} else if (hubspotPushSettings?.when_opportunity_moved_to_terminal_stage) {
		return HubspotPushModes.WHEN_OPPORTUNITY_MOVED_TO_TERMINAL_STAGE;
	}
	return HubspotPushModes.DONT_PUSH;
};

export const getFileNameFromContentDisposition = (
	contentDisposition: string
) => {
	return contentDisposition
		?.split(";")
		?.find(n => n.includes("filename="))
		?.replace("filename=", "")
		?.replace(/['"]/g, "")
		?.trim();
};

export const isNumeric = (value: string) => {
	return /^-?\d+$/.test(value);
};

export const removeVendorPrefixFromEntity = (entity?: ZomentumEntities) => {
	return entity?.replace("vendor_", "");
};

export const sanitizeFilename = (name?: string) =>
	name?.replace(/[^0-9a-z\.\u0020]/gi, "");

export const areFiltersChanged = (
	filterCriteria1: CustomViewFilterCriteria[],
	filterCriteria2: CustomViewFilterCriteria[]
) => {
	return (
		!!differenceBy(
			filterCriteria1,
			filterCriteria2,
			(criteria: CustomViewFilterCriteria) => {
				const values = criteria.values.map(value => value.id_value);
				return `${criteria.field_name}-${values.join(",")}`;
			}
		).length || filterCriteria1.length !== filterCriteria2.length
	);
};

/**
 *
 * @param date moment date to convert to ISO
 */
export const convertToISODate = (date: Moment | null) => {
	if (date) return date.utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
	return;
};

/**
 * Filters out formula fields from an array of custom fields.
 *
 * @param fields - An array of custom fields.
 * @returns An array of custom fields without formula fields.
 */
export const filterOutFormulaFields = (
	fields?: CustomField[]
): CustomField[] => {
	return (
		fields?.filter(
			field => field.field_type !== CustomFieldType.FormulaField
		) ?? []
	);
};

export const filterOutFormulaFieldFormValues = (
	fields: Record<string, string | string[]> = {},
	fieldsToKeep: string[]
) => {
	return Object.keys(fields).reduce(
		(acc: Record<string, string | string[]>, key) => {
			if (fieldsToKeep.includes(key)) {
				acc[key] = fields[key];
			}
			return acc;
		},
		{}
	);
};

export const convertImageFromLinkToDataUri = async (
	imageUrl: string
): Promise<string> => {
	try {
		const response = await fetch(imageUrl);
		const blob = await response.blob();
		const reader = new FileReader();
		return new Promise((resolve, reject) => {
			reader.onloadend = () => {
				if (typeof reader.result === "string") {
					resolve(reader.result);
				} else {
					reject(new Error("Error converting blob to Data URI"));
				}
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	} catch (error) {
		console.error("Error converting image to Data URI:", error);
		throw error;
	}
};

export const convertImageFromBlobToDataUri = async (
	blob: Blob
): Promise<string> => {
	try {
		const reader = new FileReader();
		return new Promise((resolve, reject) => {
			reader.onloadend = () => {
				if (typeof reader.result === "string") {
					resolve(reader.result);
				} else {
					reject(new Error("Error converting blob to Data URI"));
				}
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	} catch (error) {
		console.error("Error converting blob to Data URI:", error);
		throw error;
	}
};

export const getUOMAbbreviationUnit = (
	unitOfMeasure?: string,
	unitOfMeasureAbbreviation?: string
): string => {
	if (!unitOfMeasure) {
		return "";
	}
	if (!unitOfMeasureAbbreviation) {
		switch (unitOfMeasure) {
			case "Each":
				return "/ea";
			case "Case":
				return "/cs";
			case "Per Hour":
				return "/hr";
			case "Per Month":
				return "/mo";
			default:
				return "";
		}
	} else {
		return "/" + unitOfMeasureAbbreviation;
	}
};

export const getProductImageUrl = (url: string) => {
	if (url.includes("inishop.com")) {
		url += "?content_token=" + process.env.REACT_APP_ICECAT_CONTENT_TOKEN;
	}
	return url;
};

export const toTitleCase = (str: string): string => {
	return str.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
};

export const preloadImage = async (imageUrl: string): Promise<void> => {
	if (!imageUrl) return;

	const img = new Image();
	img.src = imageUrl;
	await new Promise((resolve, reject) => {
		img.onload = resolve;
		img.onerror = reject;
	});
};
